import { normalizeForMatch } from "../lib/textNormalize";

// 事前チェックの はい/いいえ 判定 (spec 2026-07-19 D17)。
// LLM (structureClient) を使わず端末側のキーワード照合で判定する:
// 事前チェックは「設定が正しいか確かめる場所」であり、LLM 障害時に wizard から
// 出られなくなる依存を持ち込みたくない。日本語の肯定/否定は閉じた語彙なので
// 照合で足りる。判定できない発話は unclear を返し、呼び出し側が聞き直す。
export type YesNo = "yes" | "no" | "unclear";

// 「お願いします」は語彙に含めない: 依頼の丁寧語は肯定・否定どちらの文にも
// 付く汎用表現で (例:「もう一度お願いします」は再依頼であって同意ではない)、
// 語彙に入れると意図しない yes/no を作ってしまう。unclear のまま呼び出し側に
// 聞き直させるのが正しい挙動。
//
// 短く曖昧な語 (いえ/いや/うん/ええ) は発話全体と完全一致したときだけ判定する
// (exact tier)。部分一致にすると「いえいり」(家入) や「うんの」(海野) のような
// 人名の一部にまで反応してしまう。長く紛れの少ない語は部分一致で拾う
// (substring tier)。「はい」は他の語彙やフィラーと衝突しないため substring
// tier のままでよい。
const YES_EXACT = ["うん", "ええ"];
const YES_SUBSTRING = [
  "はい",
  "はーい",
  "ok",
  "おーけー",
  "オーケー",
  "大丈夫",
  "聞こえました",
  "聞こえます",
  "そうです",
  "いいです",
];

const NO_EXACT = ["いえ", "いや"];
const NO_SUBSTRING = [
  "いいえ",
  "ちがいます",
  "ちがう",
  "違います",
  "違う",
  "だめ",
  "ダメ",
  "聞こえません",
  "聞こえない",
  // 「大丈夫」の否定形。じゃない/ではない が無いと「大丈夫じゃないです」が
  // 肯定語彙の「大丈夫」にだけ反応して yes に倒れてしまう。
  "じゃない",
  "ではない",
];

// 相槌・言い淀みは語彙照合の前に取り除く。取り除かないと「ええと」に含まれる
// 「ええ」のように、聞き返しの前置きが肯定と誤判定される。あのー/あの は
// 長い方を先に置き、あのー を1語として除去してから短い あの を見る。
const FILLERS = ["ええと", "えーと", "えっと", "うんと", "あのー", "あの", "まあ"];

function stripFillers(s: string): string {
  let result = s;
  for (const filler of FILLERS) {
    result = result.split(filler).join("");
  }
  return result;
}

function matchesNo(t: string): boolean {
  return NO_EXACT.includes(t) || NO_SUBSTRING.some((w) => t.includes(w));
}

function matchesYes(t: string): boolean {
  return YES_EXACT.includes(t) || YES_SUBSTRING.some((w) => t.includes(w));
}

export function parseYesNo(transcript: string): YesNo {
  const t = stripFillers(normalizeForMatch(transcript));
  if (!t) return "unclear";
  // 否定を先に見る: 「はい、でも聞こえません」「大丈夫じゃないです」のような
  // 肯定語と否定語が混在する発話は、先へ進めない方 (no) に倒すのが安全側。
  // この安全側への倒し方は「否定を先にチェックしている」という文の順序だけで
  // 成立している (no と判定した時点で return するため yes 側は評価されない)。
  // 将来この順序を入れ替えると、混在発話の判定が静かに逆転するので注意。
  if (matchesNo(t)) return "no";
  if (matchesYes(t)) return "yes";
  return "unclear";
}
